// @ts-check

/**
 * Mapbox arrow and route processing utilities.
 * Handles calculation of arrow bearings and creation of arrowhead/label features.
 */

/**
 * Calculate bearing (direction) between two coordinates in degrees.
 * @param {[number, number]} from - [longitude, latitude] of start point
 * @param {[number, number]} to - [longitude, latitude] of end point
 * @returns {number} Bearing in degrees (0-360)
 */
export function calculateBearing(from, to) {
  const [lon1, lat1] = from;
  const [lon2, lat2] = to;

  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const lat1Rad = (lat1 * Math.PI) / 180;
  const lat2Rad = (lat2 * Math.PI) / 180;

  const y = Math.sin(dLon) * Math.cos(lat2Rad);
  const x =
    Math.cos(lat1Rad) * Math.sin(lat2Rad) -
    Math.sin(lat1Rad) * Math.cos(lat2Rad) * Math.cos(dLon);

  const bearing = (Math.atan2(y, x) * 180) / Math.PI;
  return (bearing + 360) % 360;
}

/**
 * Calculate midpoint of a LineString coordinate array.
 * @param {Array<[number, number]>} coordinates - Array of [lon, lat] coordinates
 * @returns {[number, number]} Midpoint [longitude, latitude]
 */
export function calculateMidpoint(coordinates) {
  if (coordinates.length === 0) return [0, 0];
  if (coordinates.length === 1) return coordinates[0];

  const midIndex = Math.floor(coordinates.length / 2);
  return coordinates[midIndex];
}

/**
 * Extract end coordinates and bearing from a LineString feature.
 * @param {Object} feature - GeoJSON LineString feature
 * @returns {{coordinates: [number, number], bearing: number} | null} End point and bearing, or null if invalid
 */
export function getLineEndPoint(feature) {
  const coords = feature.geometry.coordinates;
  if (coords.length < 2) return null;

  const endPoint = coords[coords.length - 1];
  const secondToLast = coords[coords.length - 2];

  return {
    coordinates: endPoint,
    bearing: calculateBearing(secondToLast, endPoint)
  };
}

/**
 * Create GeoJSON FeatureCollection of arrowhead points from route features.
 * @param {Object} routesGeoJson - GeoJSON FeatureCollection of route lines
 * @returns {Object} GeoJSON FeatureCollection of arrowhead points
 */
export function createArrowheadFeatures(routesGeoJson) {
  const arrowFeatures = routesGeoJson.features
    .map((feature) => {
      const endData = getLineEndPoint(feature);
      if (!endData) return null;

      return {
        type: "Feature",
        geometry: {
          type: "Point",
          coordinates: endData.coordinates
        },
        properties: {
          ...feature.properties,
          bearing: endData.bearing
        }
      };
    })
    .filter(Boolean);

  return {
    type: "FeatureCollection",
    features: arrowFeatures
  };
}

/**
 * Create GeoJSON FeatureCollection of label points (midpoints) from route features.
 * @param {Object} routesGeoJson - GeoJSON FeatureCollection of route lines
 * @returns {Object} GeoJSON FeatureCollection of label points
 */
export function createRouteLabelFeatures(routesGeoJson) {
  const labelFeatures = routesGeoJson.features.map((feature) => {
    const coords = feature.geometry.coordinates;
    const midpoint = calculateMidpoint(coords);

    return {
      type: "Feature",
      geometry: {
        type: "Point",
        coordinates: midpoint
      },
      properties: {
        ...feature.properties
      }
    };
  });

  return {
    type: "FeatureCollection",
    features: labelFeatures
  };
}

/**
 * Filter GeoJSON features by layer_id property.
 * @param {Object} geoJson - GeoJSON FeatureCollection
 * @param {string} layerId - Layer ID to filter by (e.g., "migration_wave_1")
 * @returns {Object} Filtered GeoJSON FeatureCollection
 */
export function filterRoutesByLayerId(geoJson, layerId) {
  const filteredFeatures = geoJson.features.filter(
    (feature) => feature.properties?.layer_id === layerId
  );

  return {
    type: "FeatureCollection",
    features: filteredFeatures
  };
}

/**
 * Filter GeoJSON polygon features by layer_id property.
 * @param {Object} geoJson - GeoJSON FeatureCollection
 * @param {string} layerId - Layer ID to filter by (e.g., "migration_wave_1")
 * @returns {Object} Filtered GeoJSON FeatureCollection with only Polygon/MultiPolygon features
 */
export function filterPolygonsByLayerId(geoJson, layerId) {
  const filteredFeatures = geoJson.features.filter(
    (feature) =>
      (feature.geometry.type === "Polygon" ||
        feature.geometry.type === "MultiPolygon") &&
      feature.properties?.layer_id === layerId
  );

  return {
    type: "FeatureCollection",
    features: filteredFeatures
  };
}
